/*
 * Copyright (c) 2023 Bluemate B.V.
 */
import './style/returns.scss';

import React, {KeyboardEventHandler, MouseEventHandler, useContext} from 'react';

import {useTranslation} from 'react-i18next';

import {
    AwesomeIcon,
    ChangeFunction,
    ElementPosition,
    Loader,
    MenuPlacement,
    OptionsMenu,
    OptionsMenuItem,
    SelectItemShape
} from '@dvrd/dvr-controls';

import BolReturn from '../../models/bolReturnModel';

import {BluemateInput, BluemateSelect} from '../controls';

import {formatSimpleDate, parseThousandSeparator} from '../../util/utils';

import classNames from 'classnames';

import {ProcessingResultTranslations, ReturnCounts, ReturnProcessingResult} from '../../util/interfaces';

import {Icon} from '@iconify/react';

import optionsHorizontal from '@iconify/icons-mi/options-horizontal';

import {getReturnOptions, ReturnOption, ReturnOptionType} from '../../util/returnUtil';

import {IconName} from '@fortawesome/fontawesome-svg-core';

import Image from '../misc/image';

import {AppContext} from '../misc/appContext';

import {userHasRight} from '../../util/accessUtil';

import InfoBanner from '../misc/infoBanner';

interface Props {
    onChangeSearch: ChangeFunction<string>;
    onEnterSearch: KeyboardEventHandler;
    onClickReturn: (bolReturn: BolReturn) => MouseEventHandler;
    onClickInvoice: (invoiceId: string) => MouseEventHandler;
    onChangePage: (page: number) => MouseEventHandler;
    onChangeStatus: ChangeFunction<ReturnProcessingResult | ''>;
    onClickSend: (invoiceId: string) => MouseEventHandler;
    onClickCredit: (bolReturn: BolReturn) => MouseEventHandler;
    onClickBol: MouseEventHandler;
    returns: BolReturn[];
    loading: boolean;
    search: string;
    page: number;
    limit: number;
    total: number;
    status: ReturnProcessingResult | '';
    counts: ReturnCounts;
    loadingCounts: boolean;
}

export default function Returns(props: Props) {
    const {t} = useTranslation();
    const context = useContext(AppContext);
    const statusItems: SelectItemShape[] = [
        {label: t('returns.status.all', 'Alle'), value: ''},
        {label: t('returns.status.open', 'Openstaand'), value: ReturnProcessingResult.OPEN},
        {label: t('returns.status.accepted', 'Verwerkt'), value: ReturnProcessingResult.ACCEPTED},
        {label: t('returns.status.rejected', 'Geweigerd'), value: ReturnProcessingResult.REJECTED},
        {label: t('returns.status.canceled', 'Geannuleerd'), value: ReturnProcessingResult.CANCELED}
    ];
    const {
        loading,
        search,
        onChangeSearch,
        onEnterSearch,
        returns,
        limit,
        page,
        onClickReturn,
        onClickInvoice,
        total,
        onChangePage,
        onChangeStatus,
        status,
        loadingCounts,
        counts,
        onClickSend,
        onClickCredit,
        onClickBol
    } = props;

    function onClickStatus(type: ReturnProcessingResult) {
        return function () {
            if (type === status) onChangeStatus('');
            else onChangeStatus(type);
        };
    }

    function _getReturnOptions(bolReturn: BolReturn): OptionsMenuItem[] {
        const options = getReturnOptions(bolReturn).filter((option: ReturnOption) =>
            userHasRight(context, option.accessKey)
        );
        return options.map((option: ReturnOption) => {
            let onClick: MouseEventHandler;
            if (option.type === ReturnOptionType.CREDIT) onClick = onClickCredit(bolReturn);
            else if (option.type === ReturnOptionType.INVOICE) onClick = onClickInvoice(bolReturn.invoiceId);
            else if (option.type === ReturnOptionType.CREDIT_INVOICE)
                onClick = onClickInvoice(bolReturn.creditInvoiceId);
            else if (option.type === ReturnOptionType.SEND) onClick = onClickSend(bolReturn.creditInvoiceId);
            else if (option.type === ReturnOptionType.BOL) onClick = onClickBol;
            else onClick = onClickReturn(bolReturn);
            return {onClick, icon: option.icon, label: option.label, disabled: option.disabled};
        });
    }

    function renderReturnsContainer() {
        return (
            <div className='returns-container'>
                <Loader active={loading} />
                <div className='row head'>
                    <div />
                    <label>{t('returns.header.returnId', 'RETOUR ID')}</label>
                    <label>{t('returns.header.date', 'DATUM')}</label>
                    <label>{t('returns.header.rmaId', 'RMA ID')}</label>
                    <label>{t('returns.header.orderId', 'ORDER ID')}</label>
                    <label>{t('returns.header.status', 'STATUS')}</label>
                    <div />
                </div>
                {renderReturns()}
            </div>
        );
    }

    function renderReturns() {
        if (!returns.length) {
            return renderNotFound();
        }
        return returns.map(renderReturn);
    }

    function renderReturn(bolReturn: BolReturn, index: number) {
        const imgElement = bolReturn.product?.imageUrl ? (
                <img src={bolReturn.product.imageUrl} alt='' className='image' />
            ) : (
                <AwesomeIcon name='image' className='image-icon' prefix='far' />
            ),
            optionsRef = React.createRef<OptionsMenu>();

        function onClickRow() {
            optionsRef.current?.onOpenExternal();
        }

        return (
            <div key={index} className='row' onClick={onClickRow}>
                {imgElement}
                <label>{bolReturn.returnId}</label>
                <label>{formatSimpleDate(bolReturn.registeredAtMoment)}</label>
                <label>{bolReturn.rmaId}</label>
                <label>{bolReturn.orderId}</label>
                {renderReturnStatus(bolReturn)}
                <OptionsMenu
                    options={_getReturnOptions(bolReturn)}
                    placement={MenuPlacement.BOTTOM_LEFT}
                    optionsComponent={<Icon icon={optionsHorizontal} className='options-icon' />}
                    containerClass='common-options'
                    ref={optionsRef}
                />
            </div>
        );
    }

    function renderReturnStatus(bolReturn: BolReturn) {
        const {isCredited, translatedStatus, statusColor, creditSent} = bolReturn;
        let columns = 1;
        if (isCredited) columns++;
        if (creditSent) columns++;
        return (
            <div className='return-status' style={{gridTemplateColumns: `repeat(${columns}, fit-content(100%)`}}>
                <label className={classNames('status', statusColor)}>{translatedStatus}</label>
                {isCredited &&
                    renderStatusCircle(
                        'purple',
                        t('returns.creditInvoiceCreated', 'Creditfactuur aangemaakt'),
                        'C',
                        true
                    )}
                {creditSent &&
                    renderStatusCircle(
                        'blue',
                        t('returns.creditInvoiceSent', 'Creditfactuur verzonden'),
                        'paper-plane'
                    )}
            </div>
        );
    }

    function renderStatusCircle(
        color: string,
        label: string,
        icon: IconName | string,
        isTextIcon: boolean = false
    ): React.ReactElement {
        const iconElement = isTextIcon ? (
            <span className='status-label text'>{icon}</span>
        ) : (
            <AwesomeIcon name={icon as IconName} className='status-label' />
        );
        return (
            <div className={classNames('status-circle', color)}>
                {iconElement}
                <label className='status-full-label'>{label}</label>
            </div>
        );
    }

    function renderNotFound() {
        const terms: string[] = [];
        if (search.length) terms.push(t('returns.notFound.searchTerm', "de zoekterm '{{search}}'", {search}));
        if (status.length)
            terms.push(
                t('returns.notFound.statusTerm', 'de status {{status}}', {
                    status: ProcessingResultTranslations[status as ReturnProcessingResult]
                })
            );
        const term = terms.length
            ? ` ${t('returns.notFound.with', 'met')} ${terms.join(` ${t('returns.notFound.and', 'en')} `)} `
            : ' ';

        return (
            <div className='returns-image-container not-found'>
                <Image name='data-rows' className='returns-image' />
                <label className='image-label'>{t('returns.notFound.title', 'Geen retouren gevonden')}</label>
                <label className='image-sub-label'>
                    {t('returns.notFound.subtitle', 'Er kunnen geen retouren{{term}}gevonden worden.', {term})}
                </label>
            </div>
        );
    }

    function renderPagerCount() {
        const start = Math.min(page * limit + 1, total),
            end = Math.min(total, (page + 1) * limit);
        return (
            <label className='count-label'>
                {start}-{end} {t('returns.pager.of', 'van')} {total}
            </label>
        );
    }

    function renderPager(type: 'prev' | 'next') {
        let enabled: boolean;
        if (type === 'prev') enabled = page > 0;
        else {
            const maxPage = Math.ceil(total / limit);
            enabled = page < maxPage - 1;
        }

        let icon: IconName, changePage: number;
        if (type === 'prev') {
            icon = 'chevron-left';
            changePage = page - 1;
        } else {
            icon = 'chevron-right';
            changePage = page + 1;
        }
        return (
            <AwesomeIcon
                name={icon}
                className={classNames('pager-icon', enabled && 'enabled')}
                onClick={enabled ? onChangePage(changePage) : undefined}
            />
        );
    }

    function renderCounts() {
        return (
            <div className='counts-row'>
                {renderCount(ReturnProcessingResult.OPEN)}
                {renderCount(ReturnProcessingResult.ACCEPTED)}
                {renderCount(ReturnProcessingResult.REJECTED)}
                {renderCount(ReturnProcessingResult.CANCELED)}
            </div>
        );
    }

    function renderCount(type: ReturnProcessingResult) {
        let label: string, icon: IconName, color: string;
        const count: string = parseThousandSeparator(counts[type] ?? 0);
        const active = status === type;
        if (type === ReturnProcessingResult.OPEN) {
            label = t('returns.count.open', 'openstaand');
            icon = 'truck-loading';
            color = 'orange';
        } else if (type === ReturnProcessingResult.ACCEPTED) {
            label = t('returns.count.accepted', 'verwerkt');
            icon = 'check-circle';
            color = 'green';
        } else if (type === ReturnProcessingResult.REJECTED) {
            label = t('returns.count.rejected', 'geweigerd');
            icon = 'times-circle';
            color = 'red';
        } else {
            label = t('returns.count.canceled', 'geannuleerd');
            icon = 'ban';
            color = 'gray';
        }
        return (
            <div className={classNames('return-count', color, active && 'active')} onClick={onClickStatus(type)}>
                <Loader active={loadingCounts} />
                <AwesomeIcon name={icon} className='count-icon' />
                <label className='count-label'>
                    <span className='count-num'>{count}</span> {label}
                </label>
            </div>
        );
    }

    return (
        <>
            <InfoBanner
                featureKey='returns'
                variant='info'
                title={t('returns.banner.title', 'Retouren')}
                showForNewUsersOnly={true}
            >
                {t('returns.banner.body', 'Bekijk retour statussen van bol. Maak creditfacturen en bekijk redenen.')}
            </InfoBanner>
            <div className='returns'>
                {renderCounts()}
                <div className='returns-row'>
                    <div className='filter-bar'>
                        <BluemateInput
                            onChange={onChangeSearch}
                            value={search}
                            onEnter={onEnterSearch}
                            label={t('returns.search.label', 'Retour zoeken')}
                            inputProps={{placeholder: t('returns.search.placeholder', 'Zoeken...')}}
                            className='search-field'
                            ornaments={{
                                element: <AwesomeIcon name='search' className='search-icon' />,
                                placement: ElementPosition.LEFT
                            }}
                            unControlled
                        />
                        <BluemateSelect
                            value={status}
                            onChange={onChangeStatus}
                            items={statusItems}
                            label={t('returns.filter.statusLabel', 'Retourstatus')}
                            className='select-field'
                            dynamicWidth
                        />
                    </div>
                    {renderReturnsContainer()}
                    <div className='returns-footer'>
                        {renderPagerCount()}
                        {renderPager('prev')}
                        {renderPager('next')}
                    </div>
                </div>
            </div>
        </>
    );
}
